import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Home, Search } from 'lucide-react';

/**
 * NotFoundPage — rendu par la route catch-all `path="*"`.
 *
 * ⚠️ Limitation plateforme : le proxy Emergent renvoie toujours HTTP 200 pour
 * toute URL (catch-all SPA). Nous ne pouvons PAS forcer un vrai 404 HTTP côté
 * serveur depuis le code frontend. À défaut, nous injectons :
 *   • `<meta name="robots" content="noindex, nofollow">` (indexé/lu par Google)
 *   • `<meta name="prerender-status-code" content="404">` (lu par Rendertron et
 *     autres pré-rendeurs pour transmettre le vrai 404)
 * Google Search Console traite ces pages en "soft 404" et les déindexe.
 */
const NotFoundPage = () => {
  useEffect(() => {
    // Signal pour un éventuel prérendu server-side (Rendertron, Prerender.io…)
    if (typeof window !== 'undefined') {
      window.prerenderReady = true;
    }
  }, []);

  return (
    <>
      <Helmet>
        <title>Page introuvable — CarMaintenance</title>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="prerender-status-code" content="404" />
        <meta httpEquiv="Status" content="404 Not Found" />
        <meta name="description" content="Cette page n'existe pas ou a été déplacée. Retour à l'accueil CarMaintenance." />
      </Helmet>

      <section className="min-h-[70vh] flex items-center justify-center py-20 bg-gradient-to-br from-[#e8f5ef] via-[#f0f9f4] to-white">
        <div className="container mx-auto px-4 text-center max-w-2xl">
          <div className="inline-flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-3 py-1 rounded-full text-xs font-semibold mb-6">
            Erreur 404
          </div>
          <h1 className="text-6xl sm:text-7xl font-extrabold text-gray-900 mb-4" data-testid="notfound-heading">
            Page introuvable
          </h1>
          <p className="text-lg text-gray-600 leading-relaxed mb-8">
            Cette page n'existe pas ou a été déplacée. Utilisez les liens ci-dessous
            pour retrouver votre chemin.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/">
              <Button size="lg" className="bg-[#52a675] hover:bg-[#3d8a5c] text-white" data-testid="notfound-home-btn">
                <Home className="w-4 h-4 mr-2" />
                Retour à l'accueil
              </Button>
            </Link>
            <Link to="/abonnements">
              <Button size="lg" variant="outline" data-testid="notfound-abo-btn">
                <Search className="w-4 h-4 mr-2" />
                Voir les abonnements
              </Button>
            </Link>
          </div>
          <div className="mt-10 text-sm text-gray-500">
            <p className="mb-2">Liens utiles :</p>
            <div className="flex flex-wrap gap-x-4 gap-y-1 justify-center">
              <Link to="/nos-partenaires" className="text-[#52a675] hover:underline">Nos garages</Link>
              <Link to="/faq" className="text-[#52a675] hover:underline">FAQ</Link>
              <Link to="/contact" className="text-[#52a675] hover:underline">Contact</Link>
              <Link to="/a-propos-de-nous" className="text-[#52a675] hover:underline">Le projet</Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default NotFoundPage;
